import React, { useEffect, useState } from 'react';
import {
    Box, Button, Container, Group, Pagination, Paper, ScrollArea,
    Select, Table, Text, Title,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { BiCalendarAlt } from 'react-icons/bi';
import { RiFileExcel2Fill } from 'react-icons/ri';
import client from '../API/API';
import NoDataAvailable from '../components/noDataAvailable/NoDataAvailable';

/* ─── helpers ────────────────────────────────────────────────────── */

const toYMD = (d) => {
    if (!d) return '';
    const date = new Date(d);
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

/* flatten all channel groups into one ordered list */
const flatChannels = (record) => [
    ...(record.digital_inputs  || []),
    ...(record.digital_outputs || []),
    ...(record.analog_inputs   || []),
    ...(record.analog_outputs  || []),
    ...(record.other           || []),
];

/* ─── component ──────────────────────────────────────────────────── */

const TrailPage = () => {
    const PAGE_SIZE = 50;
    const [machines,    setMachines]    = useState([]);
    const [machineId,   setMachineId]   = useState(null);
    const [dateValue,   setDateValue]   = useState(new Date());
    const [trails,      setTrails]      = useState([]);
    const [visible,     setVisible]     = useState(false);
    const [loading,     setLoading]     = useState(false);
    const [page,        setPage]        = useState(1);
    const [totalPages,  setTotalPages]  = useState(1);
    const [totalRows,   setTotalRows]   = useState(0);

    /* load user's machines on mount */
    useEffect(() => {
        client.get('/api/machines/')
            .then(res => setMachines(res.data.machines || []))
            .catch(console.error);
    }, []);

    /* ── search ── */
    const handleSearch = async (nextPage = 1) => {
        if (!machineId) return;
        setLoading(true);
        try {
            const res = await client.get('/api/trails/', {
                params: {
                    machine_id: machineId,
                    date: toYMD(dateValue),
                    page: nextPage,
                    page_size: PAGE_SIZE,
                },
            });
            setTrails(res.data.trails || []);
            setPage(res.data.page || nextPage);
            setTotalPages(res.data.total_pages || 1);
            setTotalRows(res.data.total_records || 0);
            setVisible(true);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (nextPage) => {
        setPage(nextPage);
        handleSearch(nextPage);
    };

    /* ── export xlsx (needs Bearer token) ── */
    const handleExport = async () => {
        if (!machineId) return;
        const token  = localStorage.getItem('token');
        const dateStr = toYMD(dateValue);
        const res = await fetch(
            `/api/trails/export/?machine_id=${machineId}&date=${dateStr}`,
            { headers: { Authorization: `Bearer ${token}` } }
        );
        const blob = await res.blob();
        const url  = URL.createObjectURL(blob);
        const a    = document.createElement('a');
        a.href     = url;
        a.download = `trails_${machineId}_${dateStr}.xlsx`;
        a.click();
        URL.revokeObjectURL(url);
    };

    /* ── build dynamic column headers from first record ── */
    const colHeaders = trails.length > 0 ? flatChannels(trails[0]).map(ch => ch.name) : [];

    const machineOptions = machines.map(m => ({
        value: m.machine_id,
        label: m.machine_name || m.machine_id,
    }));

    return (
        <Container fluid ml={10} mt="0.3rem">
            {/* ── Controls ── */}
            <Box mt={20} p="1rem" pt={0}>
                <Group align="flex-end">
                    <Select
                        label="Machine"
                        placeholder="Select machine"
                        data={machineOptions}
                        value={machineId}
                        onChange={setMachineId}
                        searchable
                        clearable
                        style={{ minWidth: 200 }}
                    />
                    <DatePickerInput
                        icon={<BiCalendarAlt size="1.3rem" color="var(--color-icon)" />}
                        label="Date"
                        value={dateValue}
                        onChange={setDateValue}
                        valueFormat="YYYY-MM-DD"
                        highlightToday
                        styles={{ calendar: { width: '220px' }, day: { width: '30px' } }}
                    />
                    <Button
                        loading={loading}
                        disabled={!machineId}
                        onClick={() => handleSearch(1)}
                    >
                        Search
                    </Button>
                </Group>
            </Box>

            {/* ── Table ── */}
            {visible && (
                <Container fluid mt={10}>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '0.5rem',
                        }}
                    >
                        <Title color="var(--color-bold-text)" size="1.2rem" weight={500}>
                            Machine Data
                        </Title>
                        <RiFileExcel2Fill
                            color="var(--color-onclick)"
                            style={{ transform: 'scale(2)', cursor: 'pointer', marginRight: '1rem' }}
                            onClick={handleExport}
                            title="Export to Excel"
                        />
                    </Box>
                    <Text size="sm" color="var(--color-text)" mb="0.5rem">
                        Showing {trails.length > 0 ? ((page - 1) * PAGE_SIZE) + 1 : 0}-
                        {Math.min(page * PAGE_SIZE, totalRows)} of {totalRows} records
                    </Text>

                    <Paper shadow="xs" radius="md" p="md" className="trail-table-shell">
                        {trails.length > 0 ? (
                            <>
                                <ScrollArea offsetScrollbars h={520} className="trail-table-scroll">
                                    <Table
                                        verticalSpacing="lg"
                                        horizontalSpacing="xl"
                                        striped
                                        highlightOnHover
                                        id="trails-table"
                                        className="trail-table"
                                    >
                                        <thead style={{ position: 'sticky', top: 0, background: 'white', zIndex: 3 }}>
                                            <tr>
                                                <th style={{ position: 'sticky', left: 0, background: 'white', zIndex: 6 }}>
                                                    Timestamp
                                                </th>
                                                {colHeaders.map((name, i) => <th key={i}>{name}</th>)}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {trails.map((record, i) => (
                                                <tr key={i}>
                                                    <td style={{ position: 'sticky', left: 0, background: 'inherit', zIndex: 2 }}>
                                                        {record.timestamp}
                                                    </td>
                                                    {flatChannels(record).map((ch, j) => (
                                                        <td key={j}>{ch.value}</td>
                                                    ))}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </ScrollArea>
                                <Group position="right" p="md">
                                    <Pagination
                                        total={totalPages}
                                        value={page}
                                        onChange={handlePageChange}
                                    />
                                </Group>
                            </>
                        ) : (
                            <NoDataAvailable />
                        )}
                    </Paper>
                </Container>
            )}
        </Container>
    );
};

export default TrailPage;
